import MainLift, { AccessoryExcercise } from "./Lifts";
import { Templates } from "./TemplatePicker";
import { BbbChallengeVariant } from "./templates/BbbChallenge";
import { divideSets } from "./templates/Bodyweight";
import { BoringButBigDeload, BoringButBigVariant } from "./templates/BoringButBig";
import { FullBodyVariant } from "./templates/FullBody";
import { TemplateSchedule, WeekDayType } from "./TemplateSchedule";
import Units from './Units';

const WeekType = Object.freeze({
  Five: 0,
  Three: 1,
  One: 2,
  Deload: 3,
});

const fiveThreeOneRatios = [
  [0.65, 0.75, 0.85],
  [0.7, 0.8, 0.9],
  [0.75, 0.85, 0.95],
  [0.4, 0.5, 0.6],
];
const fiveThreeOneReps = [
  [5, 5, 5],
  [3, 3, 3],
  [5, 3, 1],
  [5, 5, 5],
];

function generateFiveThreeOneSets(lift, trainingMax, week, bastardOrder, plateCalculator, additionalOptions) {
  let setOrder;
  if (week === WeekType.Deload) {
    return deloadSets(lift, trainingMax, plateCalculator, additionalOptions);
  } else {
    if (bastardOrder) {
      setOrder = [2, 1, 0];
    } else {
      setOrder = [0, 1, 2];
    }
  }

  const ret = [];
  ret.push(...warmupSets(
    lift,
    trainingMax,
    plateCalculator,
    additionalOptions.warmup,
    additionalOptions.upperBase,
    additionalOptions.lowerBase,
    fiveThreeOneRatios[week][0] * trainingMax,
  ));
  ret.push(...setOrder.map((setIndex) => ({
    exercise: lift.name,
    repetitions: fiveThreeOneReps[week][setIndex],
    weight: plateCalculator.roundWeight(fiveThreeOneRatios[week][setIndex] * trainingMax),
    plating: plateCalculator.platingFor(fiveThreeOneRatios[week][setIndex] * trainingMax),
    isPlusSet: setIndex === 2,
  })));

  if (additionalOptions.jokerMax > 0) {
    ret.push(...generateJokerSets(lift, fiveThreeOneRatios[week][2] * trainingMax, trainingMax, additionalOptions.jokerMax, plateCalculator));
  }

  return ret;
}

function generateJokerSets(lift, baseWeight, trainingMax, jokerMax, plateCalculator) {
  const increment = trainingMax * 0.05;
  const ret = [];
  for (let incrementCount = 1; incrementCount <= jokerMax; incrementCount++) {
    ret.push({
      exercise: lift.name,
      repetitions: -1, // Joker
      weight: plateCalculator.roundWeight(baseWeight + increment * incrementCount),
      plating: plateCalculator.platingFor(baseWeight + increment * incrementCount),
    });
  }

  return ret;
}

function warmupSets(lift, trainingMax, plateCalculator, warmupType, upperBase, lowerBase, firstSetAfter) {
  const normalRatios = [0.4, 0.5, 0.6];
  const normalReps = [5, 5, 3];

  if (warmupType === 0) {
    return (new Array(3)).fill().map((_, index) => ({
      exercise: lift.name,
      repetitions: normalReps[index],
      weight: plateCalculator.roundWeight(normalRatios[index] * trainingMax),
      plating: plateCalculator.platingFor(normalRatios[index] * trainingMax),
    }));
  } else {
    const reverseRet = [];
    const increment = 0.1 * trainingMax;
    const baseWeight = lift.isUpperBody ? upperBase : lowerBase;
    let weight = firstSetAfter - increment;

    while (weight > baseWeight + 0.25 * increment) {
      reverseRet.push({
        exercise: lift.name,
        repetitions: weight > 0.5 * trainingMax ? 3 : 5,
        weight: plateCalculator.roundWeight(weight),
        plating: plateCalculator.platingFor(weight),
      });

      weight -= increment;
    }

    reverseRet.push({
      exercise: lift.name,
      repetitions: 5,
      weight: plateCalculator.roundWeight(baseWeight),
      plating: plateCalculator.platingFor(baseWeight),
    });

    reverseRet.push({
      exercise: lift.name,
      repetitions: 10,
      weight: plateCalculator.roundWeight(0),
      plating: plateCalculator.platingFor(0),
    });

    return reverseRet.reverse();
  }
}

function deloadSets(lift, trainingMax, plateCalculator, additionalOptions) {
  const deloadRatios = [
    [0.4, 0.5, 0.6],
    [0.5, 0.6, 0.7],
    [0.65, 0.76, 0.85],
    [0.4, 0.5, 0.6],
    [0.5, 0.6, 0.7],
  ];

  const deloadReps = [
    [5, 5, 5],
    [5, 5, 5],
    [3, 3, 3],
    [10, 8, 6],
    [10, 8, 6],
  ];

  const deloadType = additionalOptions.deload;
  if (deloadType >= 0 && deloadType < 5) {
    const ret = [];
    if (!additionalOptions.deloadSkipWarmup) {
      ret.push(...warmupSets(
        lift,
        trainingMax,
        plateCalculator,
        additionalOptions.warmup,
        additionalOptions.upperBase,
        additionalOptions.lowerBase,
        deloadRatios[deloadType][0] * trainingMax));
    }

    ret.push(...(new Array(3)).fill().map((_, index) => ({
      exercise: lift.name,
      repetitions: deloadReps[deloadType][index],
      weight: plateCalculator.roundWeight(deloadRatios[deloadType][index] * trainingMax),
      plating: plateCalculator.platingFor(deloadRatios[deloadType][index] * trainingMax),
    })));

    return ret;
  } else {
    const ret = [{
      exercise: lift.name,
      repetitions: 10,
      weight: plateCalculator.roundWeight(0),
      plating: plateCalculator.platingFor(0),
    }];

    const upperBase = programUnits === Units.Lbs ? 95 : 45;
    const lowerBase = programUnits === Units.Lbs ? 135 : 60;
    const firstSetWeight = lift.isUpperBody ? upperBase : lowerBase;
    ret.push({
      exercise: lift.name,
      repetitions: 5,
      weight: plateCalculator.roundWeight(firstSetWeight),
      plating: plateCalculator.platingFor(firstSetWeight),
    });

    let weight = firstSetWeight * 1.1;
    while (weight < trainingMax * 0.95) {
      ret.push({
        exercise: lift.name,
        repetitions: weight > trainingMax * 0.8 ? 1 : 3,
        weight: plateCalculator.roundWeight(weight),
        plating: plateCalculator.platingFor(weight),
      });

      weight += trainingMax * 0.1;
    }

    ret.push({
      exercise: lift.name,
      repetitions: 1,
      weight: plateCalculator.roundWeight(trainingMax),
      plating: plateCalculator.platingFor(trainingMax),
    });
    return ret;
  }
}

const lessBoringMapping = [MainLift.benchPress, MainLift.overheadPress, MainLift.deadlift, MainLift.squat];
const boringButBigAssistance = [AccessoryExcercise.pullUp, AccessoryExcercise.dumbbellRow, AccessoryExcercise.hamstringCurl, AccessoryExcercise.hangingLegRaise];
const boringButBigAssistanceReps = [10, 10, 10, 15];
const boringButBigVariantRepCount = [10, 10, 5, 3, 1];
const twoDayBoringAssistance = [AccessoryExcercise.pullUp, AccessoryExcercise.dumbbellRow, AccessoryExcercise.goodMorning, AccessoryExcercise.goodMorning,]
function generateBoringButBigSets(lift, trainingMaxes, week, options, plateCalculator) {
  let variant = options.option;
  let boringLift = (variant === BoringButBigVariant.lessBoring || variant === BoringButBigVariant.twoDaysAWeek) ? lessBoringMapping[lift.index] : lift;
  const boringMax = trainingMaxes[boringLift.index];

  const sets = [];
  if (week !== WeekType.Deload) {
    let percent = 50;
    let repCount = boringButBigVariantRepCount[variant] ?? 10;

    if (variant === BoringButBigVariant.lessBoring || variant === BoringButBigVariant.original) {
      percent = options.ratios[boringLift.index];
    } else if (variant === BoringButBigVariant.fiveByFive) {
      percent = 80;
    } else if (variant === BoringButBigVariant.fiveByThree) {
      percent = 90;
    } else if (variant === BoringButBigVariant.fiveByOne) {
      percent = 100;
    } else if (variant === BoringButBigVariant.beyondVariant1 || variant === BoringButBigVariant.beyondVariant2) {
      let isVariant2 = variant === BoringButBigVariant.beyondVariant2;
      if (week === WeekType.Five) {
        percent = 65;
      } else if (week === WeekType.Three) {
        percent = 70;
        if (isVariant2) {
          repCount = 8;
        }
      } else if (week === WeekType.One) {
        percent = 75;
        if (isVariant2) {
          repCount = 5;
        }
      }
    } else if (variant === BoringButBigVariant.twoDaysAWeek) {
      if (week === WeekType.Five) {
        percent = 50;
      } else if (week === WeekType.Three) {
        percent = 60;
      } else if (week === WeekType.One) {
        percent = 70;
      }
    }

    sets.push(...(new Array(5)).fill().map((_) => ({
      exercise: boringLift.name,
      repetitions: repCount,
      weight: plateCalculator.roundWeight(percent * 0.01 * boringMax),
      plating: plateCalculator.platingFor(percent * 0.01 * boringMax),
    })));
  } else {
    let setCount = 0;
    let reps = 10;
    const percent = 50;
    const deload = options.deload;
    if (deload === BoringButBigDeload.fiveByFive) {
      setCount = 5;
      reps = 5;
    } else if (deload === BoringButBigDeload.fiveByTen) {
      setCount = 5;
      reps = 10;
    } else if (deload === BoringButBigDeload.threeByTen) {
      setCount = 3;
      reps = 10;
    }

    sets.push(...(new Array(setCount)).fill().map((_) => ({
      exercise: boringLift.name,
      repetitions: reps,
      weight: plateCalculator.roundWeight(percent * 0.01 * boringMax),
      plating: plateCalculator.platingFor(percent * 0.01 * boringMax),
    })));
  }

  sets.push(...(new Array(5)).fill().map((_) => ({
    exercise: (variant === BoringButBigVariant.twoDaysAWeek ? twoDayBoringAssistance : boringButBigAssistance)[lift.index].name,
    repetitions: variant === BoringButBigVariant.twoDaysAWeek ? 10 : boringButBigAssistanceReps[lift.index],
  })));

  return sets;
}

const triumvirateAssistance = [
  [AccessoryExcercise.dip, AccessoryExcercise.pullUp],
  [AccessoryExcercise.dumbbellBenchPress, AccessoryExcercise.dumbbellRow],
  [AccessoryExcercise.legPress, AccessoryExcercise.hamstringCurl],
  [AccessoryExcercise.goodMorning, AccessoryExcercise.hangingLegRaise]];
const triumvirateAssistanceReps = [
  [15, 10],
  [15, 10],
  [15, 10],
  [12, 15]];
function generateTriumvirateSets(lift) {
  return triumvirateAssistance[lift.index]
    .map((exercise, index) => (new Array(5)).fill()
      .map((_) => ({
        exercise: exercise.name,
        repetitions: triumvirateAssistanceReps[lift.index][index],
      }))).flat();
}

const periodizationBibleAssistance = [
  [AccessoryExcercise.dip, AccessoryExcercise.pullUp, AccessoryExcercise.pushdowns,],
  [AccessoryExcercise.dumbbellBenchPress, AccessoryExcercise.dumbbellRow, AccessoryExcercise.tricepExtension,],
  [AccessoryExcercise.goodMorning, AccessoryExcercise.legPress, AccessoryExcercise.hangingLegRaise,],
  [AccessoryExcercise.hamstringCurl, AccessoryExcercise.legPress, AccessoryExcercise.hangingLegRaise,],
];
const periodizationBibleReps = [
  [15, 10, 15,],
  [15, 10, 15,],
  [12, 15, 15,],
  [10, 15, 15,],
];
function generatePeriodizationBibleSets(lift) {
  return periodizationBibleAssistance[lift.index]
    .map((exercise, index) => (new Array(5)).fill()
      .map((_) => ({
        exercise: exercise.name,
        repetitions: periodizationBibleReps[lift.index][index],
      }))).flat();
}

const bodyweightAssistance = [
  [AccessoryExcercise.pullUp, AccessoryExcercise.dip],
  [AccessoryExcercise.pullUp, AccessoryExcercise.pushUp],
  [AccessoryExcercise.oneLegSquat, AccessoryExcercise.sitUp],
  [AccessoryExcercise.gluteHamRaise, AccessoryExcercise.hangingLegRaise],
]
function generateBodyweightSets(lift, options) {
  const assistanceSets = divideSets(options.repCount, options.setCount);
  return bodyweightAssistance[lift.index]
    .map((exercise) => assistanceSets.map((reps) => (
      {
        exercise: exercise.name,
        repetitions: reps,
      }
    ))).flat();
}

const simplestStrengthLifts = [
  AccessoryExcercise.closeGripBench,
  AccessoryExcercise.inclinePress,
  AccessoryExcercise.frontSquat,
  AccessoryExcercise.straightLegDeadlift,
];
const simplestStrengthRatios = [
  [0.5, 0.6, 0.7],
  [0.6, 0.7, 0.8],
  [0.65, 0.75, 0.85],
  [0.4, 0.5, 0.6]
];
const simplestStrengthReps = [
  [10, 10, 10],
  [8, 8, 8],
  [5, 5, 5],
  [5, 5, 5],
];
const simplestStrengthAssistance = [
  [
    AccessoryExcercise.dumbbellRow,
    AccessoryExcercise.pullUp,
    AccessoryExcercise.pushdowns,
    AccessoryExcercise.bicepCurl
  ], [
    AccessoryExcercise.dumbbellRow,
    AccessoryExcercise.pullUp,
    AccessoryExcercise.dip,
    AccessoryExcercise.bicepCurl
  ], [
    AccessoryExcercise.gluteHamRaise,
    AccessoryExcercise.goodMorning,
    AccessoryExcercise.hangingLegRaise
  ], [
    AccessoryExcercise.hamstringCurl,
    AccessoryExcercise.goodMorning,
    AccessoryExcercise.sitUp
  ],
];
function generateSimplestStrengthSets(lift, week, options, plateCalculator) {
  const sets = []

  sets.push(...(new Array(3).fill()).map((_, index) => ({
    exercise: simplestStrengthLifts[lift.index].name,
    repetitions: simplestStrengthReps[week][index],
    weight: plateCalculator.roundWeight(simplestStrengthRatios[week][index] * options.trainingMaxes[lift.index]),
    plating: plateCalculator.platingFor(simplestStrengthRatios[week][index] * options.trainingMaxes[lift.index]),
  })));

  sets.push(...(simplestStrengthAssistance[lift.index]
    .map((exercise, index) => (new Array(3)).fill()
      .map((_) => ({
        exercise: exercise.name,
        repetitions: 12,
      }))).flat()));

  return sets;
}

const beginnerSecondLiftRatios = [0.55, 0.65, 0.75];
const beginnerSecondLiftIntermediateRatios = [0.45, 0.55, 0.65];
// The first one is null since there is no Overhead Press day in the beginner template.
const begginnerAssistance = [null, AccessoryExcercise.dip, AccessoryExcercise.bicepCurl, null, null, AccessoryExcercise.pullUp];
function generateBeginnersSets(weekdayType, trainingMaxes, week, bastardOrder, options, plateCalculator, additionalOptions) {
  const r = options.intermediate ? beginnerSecondLiftIntermediateRatios : beginnerSecondLiftRatios;
  function secondMainLift(secondLift) {
    return (new Array(3).fill()).map((_, index) => ({
      exercise: secondLift.name,
      repetitions: 5,
      weight: plateCalculator.roundWeight(trainingMaxes[secondLift.index] * r[index]),
      plating: plateCalculator.platingFor(trainingMaxes[secondLift.index] * r[index]),
    }));
  }

  const sets = [];
  if (weekdayType.mainLift === MainLift.squat) {
    sets.push(...secondMainLift(MainLift.benchPress));
  } else if (weekdayType.mainLift === MainLift.benchPress) {
    sets.push(...secondMainLift(MainLift.squat));
  } else if (weekdayType.mainLift === MainLift.deadlift) {
    sets.push(...generateFiveThreeOneSets(MainLift.overheadPress, trainingMaxes[MainLift.overheadPress.index], week, bastardOrder, plateCalculator, additionalOptions));
  }

  sets.push(...(new Array(3).fill()).map((_) => (
    {
      exercise: begginnerAssistance[weekdayType.index].name,
      repetitions: 10,
    })));

  return sets;
}

// The first one is null since there is no Overhead Press day in the full body template.
const fullBodyAssistance = [null, [AccessoryExcercise.pullUp], [AccessoryExcercise.dumbbellBenchPress, AccessoryExcercise.dumbbellRow], null, null, []];
const fullBodyPhaseRatios = [
  [0.4, 0.5, 0.6],
  [0.65, 0.75, 0.85],
  [0.75, 0.85, 0.95],
];
const fullBodyPhaseReps = [
  [5, 5, 5],
  [3, 3, 3],
  [3, 3, 1],
];
const fullBodyUpdatedReps = [
  [5, 5, 5],
  [3, 3, 3],
  [5, 3, 1],
  [1, 1, 1],
];
const fullBodyUpdatedRatios = [
  [0.65, 0.75, 0.85],
  [0.7, 0.8, 0.9],
  [0.75, 0.85, 0.95],
  [0.8, 0.9, 1.0],
];

function generateFullBodySets(weekdayType, trainingMaxes, week, bastardOrder, options, plateCalculator, additionalOptions) {
  function getRatio(lift, index) {
    if (options.option === FullBodyVariant.original) {
      return fullBodyPhaseRatios[options.phase][index];
    } else {
      return fullBodyUpdatedRatios[options.ratios[lift.index - 1]][index];
    }
  }
  function getRep(lift, index) {
    if (options.option === FullBodyVariant.original) {
      return fullBodyPhaseReps[options.phase][index];
    } else if (lift === MainLift.deadlift && options.ratios[2] === 0) {
      return 3;
    } else {
      return fullBodyUpdatedReps[options.ratios[lift.index - 1]][index];
    }
  }

  if (options.option === FullBodyVariant.fullBoring) {
    return [MainLift.squat, MainLift.benchPress, MainLift.deadlift].flatMap((lift) => {
      if (weekdayType.mainLift === lift) {
        return generateFiveThreeOneSets(lift, trainingMaxes[lift.index], week, bastardOrder, plateCalculator, additionalOptions);
      } else {
        return (new Array(3).fill()).map((_, index) => ({
          exercise: lift.name,
          repetitions: getRep(lift, index),
          weight: plateCalculator.roundWeight(trainingMaxes[lift.index] * getRatio(lift, index)),
          plating: plateCalculator.platingFor(trainingMaxes[lift.index] * getRatio(lift, index)),
        }));
      }
    });
  }

  const sets = [];
  if (weekdayType.mainLift !== MainLift.squat && week !== WeekType.Deload) {
    sets.push(...(new Array(3).fill()).map((_, index) => ({
      exercise: MainLift.squat.name,
      repetitions: getRep(MainLift.squat, index),
      weight: plateCalculator.roundWeight(trainingMaxes[MainLift.squat.index] * getRatio(MainLift.squat, index)),
      plating: plateCalculator.platingFor(trainingMaxes[MainLift.squat.index] * getRatio(MainLift.squat, index)),
    })));
  }

  if (weekdayType.mainLift === MainLift.deadlift) {
    sets.push(...generateFiveThreeOneSets(MainLift.overheadPress, trainingMaxes[MainLift.overheadPress.index], week, bastardOrder, plateCalculator, additionalOptions));
  }

  sets.push(...generateFiveThreeOneSets(weekdayType.mainLift, trainingMaxes[weekdayType.mainLift.index], week, bastardOrder, plateCalculator, additionalOptions));

  if (options.option !== FullBodyVariant.fullBoring) {
    sets.push(...(fullBodyAssistance[weekdayType.index]
      .map((exercise) => (new Array(3)).fill()
        .map((_) => ({
          exercise: exercise.name,
          repetitions: 10,
        }))).flat()));
  }

  return sets;
}

class AssistanceSetGroup {
  constructor(exercise, setCount, repCount) {
    this.exercise = exercise;
    this.setCount = setCount;
    this.repCount = repCount;
  }

  asSets = () => {
    return (new Array(this.setCount)).fill().map((_) => ({
      exercise: this.exercise.name,
      repetitions: this.repCount,
    }))
  }
}

const twoDaysPercentages = [
  [0.65, 0.75, 0.85],
  [0.70, 0.80, 0.90],
  [0.75, 0.85, 0.95],
  [0.80, 0.90, 1.00],
];


const twoDaysReps = [
  [3, 3, 3],
  [3, 3, 3],
  [5, 3, 1],
  [1, 1, 1]
];

const twoDayAssistance = [
  [
    null,
    null,
    [
      new AssistanceSetGroup(AccessoryExcercise.pullUp, 3, 10),
      new AssistanceSetGroup(AccessoryExcercise.backRaise, 3, 15,),
      new AssistanceSetGroup(AccessoryExcercise.dumbbellBenchPress, 3, 10),
      new AssistanceSetGroup(AccessoryExcercise.rearLatRaises, 3, 20)
    ],
    [
      new AssistanceSetGroup(AccessoryExcercise.dumbbellRow, 3, 10),
      new AssistanceSetGroup(AccessoryExcercise.dip, 3, 10),
      new AssistanceSetGroup(AccessoryExcercise.goodMorning, 3, 10),
      new AssistanceSetGroup(AccessoryExcercise.bicepCurl, 3, 10)
    ]
  ], [
    [
      new AssistanceSetGroup(MainLift.benchPress, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.dumbbellRow, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.dumbbellSideBend, 5, 10),],
    [
      new AssistanceSetGroup(MainLift.overheadPress, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.pullUp, 6, 10),
      new AssistanceSetGroup(AccessoryExcercise.abWheel, 5, 10),
    ],
    [
      new AssistanceSetGroup(AccessoryExcercise.frontSquat, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.straightLegDeadlift, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.hangingLegRaise, 5, 10),
    ],
    [
      new AssistanceSetGroup(MainLift.squat, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.goodMorning, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.sitUp, 5, 15),
    ],
  ], [
    [
      new AssistanceSetGroup(AccessoryExcercise.dumbbellRow, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.dumbbellSideBend, 5, 10),],
    [
      new AssistanceSetGroup(AccessoryExcercise.pullUp, 6, 10),
      new AssistanceSetGroup(AccessoryExcercise.abWheel, 5, 10),
    ],
    [
      new AssistanceSetGroup(AccessoryExcercise.hamstringCurl, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.hangingLegRaise, 5, 10),
    ],
    [
      new AssistanceSetGroup(AccessoryExcercise.goodMorning, 5, 10),
      new AssistanceSetGroup(AccessoryExcercise.sitUp, 5, 15),
    ],
  ]
];

function generateTwoDaysAWeekSets(weekdayType, trainingMaxes, week, bastardOrder, options, plateCalculator, additionalOptions) {
  function _optionThreeSets(lift, trainingMax, percentageIndex) {
    return (new Array(3)).fill().map((_, index) => ({
      exercise: lift.name,
      repetitions: twoDaysReps[percentageIndex][index],
      weight: plateCalculator.roundWeight(trainingMax * twoDaysPercentages[percentageIndex][index]),
      plating: plateCalculator.platingFor(trainingMax * twoDaysPercentages[percentageIndex][index]),
    }));
  }

  const sets = [];

  if (options.option === 0) {
    if (weekdayType.index === WeekDayType.deadliftOverheadPress.index) {
      sets.push(...generateFiveThreeOneSets(MainLift.deadlift, trainingMaxes[MainLift.deadlift.index], week, bastardOrder, plateCalculator, additionalOptions));
      sets.push(...generateFiveThreeOneSets(MainLift.overheadPress, trainingMaxes[MainLift.overheadPress.index], week, bastardOrder, plateCalculator, additionalOptions));
    } else {
      sets.push(...generateFiveThreeOneSets(MainLift.squat, trainingMaxes[MainLift.squat.index], week, bastardOrder, plateCalculator, additionalOptions));
      sets.push(...generateFiveThreeOneSets(MainLift.benchPress, trainingMaxes[MainLift.benchPress.index], week, bastardOrder, plateCalculator, additionalOptions));
    }
  } else if (options.option === 1) {
    sets.push(...generateFiveThreeOneSets(weekdayType.mainLift, trainingMaxes[weekdayType.mainLift.index], week, bastardOrder, plateCalculator, additionalOptions));
  } else if (options.option === 2) {
    if (weekdayType.mainLift === MainLift.deadlift) {
      sets.push(..._optionThreeSets(MainLift.squat, trainingMaxes[MainLift.squat.index], options.percentageIndex));
    }
    if (weekdayType.mainLift === MainLift.overheadPress) {
      sets.push(..._optionThreeSets(MainLift.benchPress, trainingMaxes[MainLift.benchPress.index], options.percentageIndex));
    }

    sets.push(...generateFiveThreeOneSets(weekdayType.mainLift, trainingMaxes[weekdayType.mainLift.index], week, bastardOrder, plateCalculator, additionalOptions));

    if (weekdayType.mainLift === MainLift.squat) {
      sets.push(..._optionThreeSets(MainLift.deadlift, trainingMaxes[MainLift.deadlift.index], options.percentageIndex));
    }
    if (weekdayType.mainLift === MainLift.benchPress) {
      sets.push(..._optionThreeSets(MainLift.overheadPress, trainingMaxes[MainLift.overheadPress.index], options.percentageIndex));
    }
  }

  sets.push(...(twoDayAssistance[options.option][weekdayType.mainLift.index].flatMap((sg) => sg.asSets())));

  return sets;
}

function generatePyramidSets(weekdayType, trainingMaxes, week, plateCalculator) {
  const repsMapping = [
    [5, 5],
    [3, 3],
    [3, 5],
  ];

  const ratioMapping = [
    [0.75, 0.65],
    [0.8, 0.7],
    [0.85, 0.75],
  ];

  const weight = trainingMaxes[weekdayType.mainLift.index];

  if (week !== WeekType.Deload) {
    return [{
      exercise: weekdayType.mainLift.name,
      repetitions: repsMapping[week][0],
      weight: plateCalculator.roundWeight(weight * ratioMapping[week][0]),
      plating: plateCalculator.platingFor(weight * ratioMapping[week][0]),
    }, {
      exercise: weekdayType.mainLift.name,
      repetitions: repsMapping[week][1],
      weight: plateCalculator.roundWeight(weight * ratioMapping[week][1]),
      plating: plateCalculator.platingFor(weight * ratioMapping[week][1]),
      isPlusSet: true,
    },];
  }
  return [];
}

function generateFirstSetLastSets(weekdayType, trainingMaxes, week, options, plateCalculator) {
  const ratioMapping = [0.65, 0.7, 0.75];
  if (week !== WeekType.Deload) {
    if (options.option === 0) {
      return [
        {
          exercise: weekdayType.mainLift.name,
          repetitions: 0, // AMRAP
          weight: plateCalculator.roundWeight(trainingMaxes[weekdayType.mainLift.index] * ratioMapping[week]),
          plating: plateCalculator.platingFor(trainingMaxes[weekdayType.mainLift.index] * ratioMapping[week]),
        }
      ];
    } else {
      return (new Array(options.setCount)).fill().map((_) => ({
        exercise: weekdayType.mainLift.name,
        repetitions: options.repCount,
        weight: plateCalculator.roundWeight(trainingMaxes[weekdayType.mainLift.index] * ratioMapping[week]),
        plating: plateCalculator.platingFor(trainingMaxes[weekdayType.mainLift.index] * ratioMapping[week]),
      }));
    }
  }
  return [];
}

function generateGvtSets(weekdayType, trainingMaxes, week, options, plateCalculator) {
  const assistance = [
    new AssistanceSetGroup(AccessoryExcercise.latPulldown, 10, 10),
    new AssistanceSetGroup(AccessoryExcercise.dumbbellRow, 10, 10),
    new AssistanceSetGroup(AccessoryExcercise.abWheel, 5, 10),
    new AssistanceSetGroup(AccessoryExcercise.hangingLegRaise, 5, 15),
  ];

  let lift = options.lessBoring ? lessBoringMapping[weekdayType.mainLift.index] : weekdayType.mainLift;
  let sets = [];
  if (week !== WeekType.Deload) {
    let percent = options.ratios[lift.index];

    sets.push(...(new Array(10)).fill().map((_) => ({
      exercise: lift.name,
      repetitions: 10,
      weight: plateCalculator.roundWeight(trainingMaxes[lift.index] * percent * 0.01),
      plating: plateCalculator.platingFor(trainingMaxes[lift.index] * percent * 0.01),
    })));
  }

  sets.push(...assistance[lift.index].asSets());
  return sets;
}

function generateFivesProgressionSets(weekdayType, trainingMaxes, week, bastardOrder, plateCalculator, additionalOptions) {
  let setOrder;
  const lift = weekdayType.mainLift;
  const trainingMax = trainingMaxes[weekdayType.mainLift.index];

  if (week === WeekType.Deload) {
    return deloadSets(lift, trainingMax, plateCalculator, additionalOptions);
  } else {
    if (bastardOrder) {
      setOrder = [2, 1, 0];
    } else {
      setOrder = [0, 1, 2];
    }
  }


  const ret = [];
  ret.push(...warmupSets(
    lift,
    trainingMax,
    plateCalculator,
    additionalOptions.warmup,
    additionalOptions.upperBase,
    additionalOptions.lowerBase,
    fiveThreeOneRatios[week][0] * trainingMax,
  ));
  ret.push(...setOrder.map((setIndex) => ({
    exercise: lift.name,
    repetitions: 5,
    weight: plateCalculator.roundWeight(fiveThreeOneRatios[week][setIndex] * trainingMax),
    plating: plateCalculator.platingFor(fiveThreeOneRatios[week][setIndex] * trainingMax),
  })));

  if (additionalOptions.jokerMax > 0) {
    ret.push(...generateJokerSets(lift, fiveThreeOneRatios[week][2] * trainingMax, trainingMax, additionalOptions.jokerMax, plateCalculator));
  }

  return ret;
}

function generateBbbChallengeSets(weekdayType, trainingMaxes, week, options, subcycle, threeFiveOne, plateCalculator) {
  let lift = options.lessBoring ? lessBoringMapping[weekdayType.mainLift.index] : weekdayType.mainLift;
  const setCount = week === WeekType.Deload ? 3 : 5;

  // We do this so that the progression stays the same even with "3/5/1". It would be
  // kind of wrong to shuffle the progression.
  let weekIndex = week;
  if (threeFiveOne) {
    if (weekIndex === 1) {
      weekIndex = 0;
    } else if (weekIndex === 0) {
      weekIndex = 1;
    }
  }

  const challengeLength = options.option;
  let ratio;
  if (weekIndex === 3) {
    ratio = 0.5;
  } else if (challengeLength === BbbChallengeVariant.sixWeek) {
    if (subcycle === 0) {
      if (weekIndex === 0 || weekIndex === 1) {
        ratio = 0.5;
      } else {
        ratio = 0.6;
      }
    } else {
      if (weekIndex === 0) {
        ratio = 0.6;
      } else {
        ratio = 0.7;
      }
    }
  } else if (challengeLength === BbbChallengeVariant.threeMonths) {
    ratio = 0.5 + (subcycle * 0.1);
  } else {
    if (weekIndex === 3) {
      ratio = 0.5;
    } else if (subcycle === 0) {
      if (weekIndex === 0 || weekIndex === 1) {
        ratio = 0.5;
      } else {
        ratio = 0.6;
      }
    } else if (subcycle === 1) {
      if (weekIndex === 0) {
        ratio = 0.6;
      } else {
        ratio = 0.7;
      }
    } else if (subcycle === 2) {
      if (weekIndex === 0 || weekIndex === 1) {
        ratio = 0.8;
      } else {
        ratio = 0.9;
      }
    } else {
      if (weekIndex === 0) {
        ratio = 0.9;
      } else {
        ratio = 1.0;
      }
    }
  }

  let reps = 10;
  if (challengeLength === BbbChallengeVariant.thirteenWeeks) {
    if (subcycle === 2) {
      if (weekIndex === 0 || weekIndex === 1) {
        reps = 5;
      } else {
        reps = 3;
      }
    } else if (subcycle === 3) {
      if (weekIndex === 0) {
        reps = 3;
      } else {
        reps = 1;
      }
    }
  }

  const sets = [];
  const trainingMax = trainingMaxes[lift.index];
  sets.push(...(new Array(setCount)).fill().map((_) => ({
    exercise: lift.name,
    repetitions: reps,
    weight: plateCalculator.roundWeight(ratio * trainingMax),
    plating: plateCalculator.platingFor(ratio * trainingMax),
  })));

  const assistance = [
    [new AssistanceSetGroup(AccessoryExcercise.pullUp, 5, 10), new AssistanceSetGroup(AccessoryExcercise.bicepCurl, 5, 10)],
    [new AssistanceSetGroup(AccessoryExcercise.dumbbellRow, 5, 10), new AssistanceSetGroup(AccessoryExcercise.rearLatRaises, 5, 10)],
    [new AssistanceSetGroup(AccessoryExcercise.hangingLegRaise, 5, 10),],
    [new AssistanceSetGroup(AccessoryExcercise.abWheel, 5, 15),],
  ];

  sets.push(...assistance[lift.index].flatMap((assistanceSetGroup) => assistanceSetGroup.asSets()));

  return sets;
}

function generateWorkout(weekdayType, trainingMaxes, week, bastardOrder, template, plateCalculator, subcycleIndex, threeFiveOne, additionalOptions) {
  const sets = [];
  const mainLiftTrainingMax = trainingMaxes[weekdayType.mainLift.index];

  // The Full Body template uses a different pattern then the classic 5/3/1 -> assistance.
  // So this one takes over the whole generation.
  if (template.pick === Templates.FullBody) {
    return {
      sets: generateFullBodySets(weekdayType, trainingMaxes, week, bastardOrder, template.fullBodyOptions, plateCalculator, additionalOptions),
    };
  } else if (template.pick === Templates.TwoDaysAWeek) {
    return {
      sets: generateTwoDaysAWeekSets(weekdayType, trainingMaxes, week, bastardOrder, template.twoDayAWeekOptions, plateCalculator, additionalOptions),
    }
  } else if (template.pick === Templates.FivesProgression) {
    return {
      sets: generateFivesProgressionSets(weekdayType, trainingMaxes, week, bastardOrder, plateCalculator, additionalOptions),
    }
  }

  sets.push(...generateFiveThreeOneSets(weekdayType.mainLift, mainLiftTrainingMax, week, bastardOrder, plateCalculator, additionalOptions));

  if (template.pick === Templates.BoringButBig) {
    sets.push(...generateBoringButBigSets(weekdayType.mainLift, trainingMaxes, week, template.boringButBigOptions, plateCalculator));
  } else if (template.pick === Templates.Triumvirate) {
    sets.push(...generateTriumvirateSets(weekdayType.mainLift));
  } else if (template.pick === Templates.PeriodizationBible) {
    sets.push(...generatePeriodizationBibleSets(weekdayType.mainLift));
  } else if (template.pick === Templates.Bodyweight) {
    sets.push(...generateBodyweightSets(weekdayType.mainLift, template.bodyweightOptions));
  } else if (template.pick === Templates.SimplestStrength) {
    sets.push(...generateSimplestStrengthSets(weekdayType.mainLift, week, template.simplestStrengthOptions, plateCalculator));
  } else if (template.pick === Templates.Beginners) {
    sets.push(...generateBeginnersSets(weekdayType, trainingMaxes, week, bastardOrder, template.beginnersOptions, plateCalculator, additionalOptions));
  } else if (template.pick === Templates.Pyramid) {
    sets.push(...generatePyramidSets(weekdayType, trainingMaxes, week, plateCalculator));
  } else if (template.pick === Templates.FirstSetLast) {
    sets.push(...generateFirstSetLastSets(weekdayType, trainingMaxes, week, template.firstSetLastOptions, plateCalculator));
  } else if (template.pick === Templates.Gvt) {
    sets.push(...generateGvtSets(weekdayType, trainingMaxes, week, template.gvtOptions, plateCalculator));
  } else if (template.pick === Templates.BbbChallenge) {
    sets.push(...generateBbbChallengeSets(weekdayType, trainingMaxes, week, template.bbbChallengeOptions, subcycleIndex, threeFiveOne, plateCalculator));
  }

  return {
    sets: sets,
  };
}

function getWeekOrder(schedule, skipDeload) {
  let ret;
  if (schedule.threeFiveOne) {
    ret = [WeekType.Three, WeekType.Five, WeekType.One];
  } else {
    ret = [WeekType.Five, WeekType.Three, WeekType.One];
  }

  if (!skipDeload) {
    ret.push(WeekType.Deload);
  }

  return ret;
}

/// Screw it. We're doing globals... (Mostly because I'm too lazy to carry it all
/// the way down to the deload sets.)
let programUnits = Units.Lbs;
function generateProgram(baseTrainingMaxes, template, additionalOptions, plateCalculator, schedule, title, unit, urlGetter) {
  programUnits = unit;
  let weeks = [];

  let lowerBodyIncrement = (unit === Units.Lbs) ? 10 : 5;
  let upperBodyIncrement = (unit === Units.Lbs) ? 5 : 2.5;

  const templateSchedule = TemplateSchedule.for(template);
  let subcycleCount = templateSchedule.numberOfSubcycles;
  for (let subcycleIndex = 0; subcycleIndex < subcycleCount; subcycleIndex++) {
    const skipDeload = (templateSchedule.subcycleDeload.indexOf(subcycleIndex) === -1) || (subcycleIndex === subcycleCount - 1 && additionalOptions.deload === -1);
    const weekOrder = getWeekOrder(schedule, skipDeload);
    let weekIndex = 0;

    let workingWeek = [];
    let trainingMaxes = baseTrainingMaxes.map((val, index) => val + (index < 2 ? upperBodyIncrement : lowerBodyIncrement) * subcycleIndex);
    while (weekIndex < weekOrder.length) {
      for (let liftIndex = 0; liftIndex < schedule.liftsOrder.length; liftIndex++) {
        workingWeek.push(generateWorkout(
          schedule.liftsOrder[liftIndex],
          trainingMaxes,
          weekOrder[weekIndex],
          schedule.bastardOrder,
          template,
          plateCalculator,
          subcycleIndex,
          schedule.threeFiveOne,
          additionalOptions,
        ));

        if ((schedule.daysAWeek === 3 &&
          weekIndex === 3 &&
          liftIndex === 0 &&
          schedule.liftsOrder.length === 4) ||
          (schedule.daysAWeek === 2 &&
            weekIndex === 3 &&
            (liftIndex === 0 || liftIndex === 2) &&
            schedule.liftsOrder.length === 4)) {
          workingWeek[workingWeek.length - 1].sets.push(...(generateWorkout(
            schedule.liftsOrder[liftIndex + 1],
            trainingMaxes,
            weekOrder[weekIndex],
            schedule.bastardOrder,
            template,
            plateCalculator,
            subcycleIndex,
            schedule.threeFiveOne,
            additionalOptions,
          ).sets));

          liftIndex++;
        }

        if (workingWeek.length === schedule.daysAWeek) {
          weeks.push(workingWeek);
          workingWeek = [];
        }
      }
      weekIndex++;
    }
  }

  return { weeks: weeks, title: title, urlGetter: urlGetter };
}

export default generateProgram;